<template>
    <Modal
        @close="$emit('close')">
        <template slot="header">
            {{ content.createNewEntry }}
        </template>
        <template slot="body">
            <LoadingOverlay
                v-if="upload.status"
                :label="upload.progress + '%'"
                :progress="upload.progress"/>

            <MovingBubbles/>

            <NewEntryMedia
                :entryPreview="entryPreview"
                :content="content"
                :mediaType="mediaType"
                @updatePayload="updatePayload"
                @setPosition="setPosition"/>

            <section class="block">
                <div class="relatedFields">
                    <div class="horizontalFields detailInput">
                        <TextField class="title"
                            v-model="title.en"
                            id="title"
                            size="large"
                            font="secondary"
                            :label="content.title"
                            :maxlength="constants.limits.entry.title"/>
                        <TextField class="title"
                            v-model="subtitle.en"
                            id="subtitle"
                            size="large"
                            font="secondary"
                            :label="content.subtitle"
                            :maxlength="constants.limits.entry.subtitle"/>
                    </div>
                    <TextField class="detailInput"
                        v-model="intro.en"
                        id="intro"
                        type="textarea"
                        size="medium"
                        :label="content.intro"
                        :maxlength="constants.limits.entry.intro"
                        :isCharCountOn="true"/>
                    <TextField class="detailInput"
                        v-model="story.en"            
                        id="story"
                        type="textarea"
                        :label="content.story"
                        :maxlength="constants.limits.entry.story"
                        :isCharCountOn="true"/>
                </div>
            </section>

            <section class="block advanced">
                <div class="toggleAdvancedOptions"
                    tabindex="0"
                    @click="isAdvancedOpen=!isAdvancedOpen">
                    <p class="advancedOptionsLabel">
                        {{ content.advancedOptions }}
                    </p>
                    <Icon
                        :icon="collapsibeIcon"
                        color ="inherit"/>
                </div>

                <div class="block advancedFieldContainer"
                    v-if="isAdvancedOpen">
                    <div class="toggleTile toggleSensitive">
                        <Toggle
                            v-model="sensitive"
                            ref="sensitiveEdit"
                            :label="content.sensitiveContent">
                            <template slot="description">
                                {{ content.sensitiveContentDescription }}
                            </template>
                        </Toggle>
                    </div>

                    <DatePicker
                        :label="content.historicalTime"
                        :description="content.historicalTimeDescription"
                        @setNewDate="setHistoricalTime"/>
                </div>
            </section>

            <section class="toggleTile toggleVisibility">
                <Toggle
                    v-model="visibility"
                    ref="visibilityEdit"
                    :label="content.publishEntry">
                </Toggle>
            </section>
        </template>
        <template class="uploadButton"
            slot="footer">
            <Button
                prefixIcon="file_upload"
                :disabled="uploadButtonState"
                @click="createEntry">
                {{ content.createEntry }}
            </Button>
        </template>
    </Modal>
</template>

<script>
    import { db } from '@/firebase'
    import { mapActions } from 'vuex'

    import content from '@/components/journal/main/lang/entries.js'

    import MovingBubbles from '@/components/templates/MovingBubbles'
    import NewEntryMedia from '@/components/journal/main/NewEntryMedia'

    export default {
        name: 'NewEntryModal',
        components: {
            MovingBubbles,
            NewEntryMedia
        },
        props: {
            mediaType: {
                type: String,
                required: true
            }
        },
        data() {
            return {
                isSlugTaken: false,
                isAdvancedOpen: false,
                upload: {
                    status: false,
                    progress: 0
                },

                title: {
                    en: '',
                    hu: ''
                },
                subtitle: {
                    en: '',
                    hu: ''
                },
                intro: {
                    en: '',
                    hu: ''
                },
                story: {
                    en: '',
                    hu: ''
                },
                storywriter: '',
                historicalTime: new Date(),
                visibility: false,
                sensitive: false,
                position: {
                    portrait:  'center',
                    landscape: 'center'
                },
                media: { },
                payload: {
                    files: [],
                    filePaths: []
                },
                followers: {
                    user: []
                }
            }
        },
        computed: {
            entryPreview() {
                return {
                    id: null,
                    content: {
                        title: {
                            en: this.title.en
                        },
                        subtitle: {
                            en: this.subtitle.en
                        },
                        intro: {
                            en: this.intro.en
                        },
                    },
                    creator: {
                        artist: {
                            avatar: this.loggedInUser.avatar,
                            name:   this.loggedInUser.name
                        }
                    }
                }
            },
            collapsibeIcon() {
                if(this.isAdvancedOpen) {
                    return 'expand_less'
                } else {
                    return 'expand_more'
                }
            },
            content() {
                return this.getContent(content)
            },
            uploadButtonState() {
                if(this.mediaType === 'carousel') {
                    if(this.payload.files.length === 0) {
                        return true
                    }
                } else if(this.mediaType === 'slider') {
                    if(this.payload.files.length === 2) {
                        return false
                    } else {
                        return true
                    }
                }
            }
        },
        methods: {
            ...mapActions('journal',['uploadFileToStore','createJournalEntry']),
            ...mapActions('notifications',['createNotification']),

            updatePayload(p) {
                this.payload = p
            },
            setHistoricalTime(e) {
                this.historicalTime = e
            },
            setPosition(pos) {
                this.position = pos
            },
            async createEntry() {
                try {
                    this.upload.status = true

                    var images = []
                    for(let i = 0; i < this.payload.files.length; i++) {
                        this.$nextTick(() => {
                            var progress = this.upload.progress + (100 / this.payload.files.length)
                            this.upload.progress = Math.round(progress)
                        })

                        let image = await this.uploadFileToStore({
                            path: this.payload.filePaths[i],
                            file: this.payload.files[i]
                        })
                        images.push(image)
                    }

                    if(this.mediaType === 'carousel') {
                        var media = {
                            carousel: []
                        }

                        for(let i = 0; i < images.length; i++) {
                            let carouselInstance = {
                                url: images[i],
                                position: this.position
                            }
                            
                           media.carousel.push(carouselInstance)
                        }
                        
                        this.media = media
                    } else if(this.mediaType === 'slider') {
                        let media = {
                            slider: {
                                left: {
                                    url: images[0],
                                    position: {
                                        landscape: 'center',
                                        portrait: 'center'
                                    }
                                },
                                right: {
                                    url: images[1],
                                    position: {
                                        landscape: 'center',
                                        portrait: 'center'
                                    }
                                }
                            }
                        }

                        this.media = media
                    }

                    var followersRef = await db
                        .collection('users')
                        .doc(this.auth.id)
                        .collection('social')
                        .doc('followers')
                        .get()

                    this.followers = {
                        ...followersRef.data()
                    }

                    var entry = {
                        media: this.media,
                        content: {
                            title:          this.title,
                            subtitle:       this.subtitle,
                            intro:          this.intro,
                            story:          this.story,
                        },
                        creator: {
                            artist:         this.auth.id,
                            storywriter:    this.auth.id,
                        },
                        flag: {
                            visibility:     this.visibility,
                            sensitive:      this.sensitive,
                            featured:       false,
                        },
                        activity: {
                            historicalTime: this.historicalTime,
                            uploadTime:     new Date(),
                            initialPublish: this.visibility,
                        },
                        extra: {
                            relatedPost:    ''
                        },
                        recipients: this.followers.users
                    }
                    
                    var entryID = await this.createJournalEntry(entry)

                    if(entryID === null) {
                        this.isSlugTaken = true
                    } else if(entryID) {
                        if(this.visibility) {
                            try {
                                var notification = {
                                    type:       'newEntry',
                                    docID:       entryID,
                                    title:       this.title,
                                    createdBy:   this.auth.id,
                                    timestamp:   new Date(),
                                    recipients:  this.followers.users
                                }

                                await this.createNotification({
                                    notification: notification
                                })
                            } catch(error) {
                                throw error
                            }
                        }
                        this.$toasted.show(this.siteContent.entryUploadSuccess)
                        this.$emit('close')
                        this.$nextTick(() => {
                            this.$router.push('/entry/' + entryID)
                        })
                    } else {
                        this.$toasted.show(this.siteContent.entryUploadError)
                    }
                } catch(error) {
                    throw error
                } finally {
                    this.upload.status = false
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../scss/variables';

    .entryPreview {
        font-family: $font-secondary;
        color: var(--color-grey-600);
        font-weight: 700;
        font-size: 1rem;
        margin: 1rem 0 2.5rem 0;
        text-align: center;
    }

    .detailInput {
        margin-bottom: 2rem;
    }

    .horizontalFields {
        display: flex;
    }

    .relatedFields {
        margin-bottom: 1.5rem;
        
        .detailInput {
            margin-bottom: 0.25rem;

            &:last-of-type {
                margin-bottom: 0;
            }
        }
    }

    .title {
        &:first-of-type {
            margin-right: 0.125rem;
        }

        &:last-of-type {
            margin-left: 0.125rem;
        }
    }

    .advancedOptionsLabel {
        width: fit-content;
        font-family: $font-secondary;
        font-weight: 700;
        font-size: 1.075rem;
        margin: 0;
    }

    .block {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .advanced {
        border-radius: 1rem;
        background: var(--color-grey-100);
        margin-bottom: 1.5rem;
    }

    .advancedFieldContainer {
        padding: 0 1.5rem 1.5rem 1.5rem;
    }

    .toggleAdvancedOptions {
        width: 100%;
        border-radius: 1rem;
        padding: 1.25rem 1.5rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
        transition: $transition-eio-20;

        &:hover {
            background: var(--color-grey-150);
        }

        &:focus {
            outline: 0;
            box-shadow: var(--shadow-focus-s);
        }
    }

    .toggleTile {
        width: 100%;
        border-radius: 1rem;
        padding: 1rem;
    }

    .toggleSensitive {
        margin-bottom: 1rem;
    }

    .toggleVisibility {
        background: var(--color-grey-100);
        padding: 1rem 1.5rem;
    }

    .uploadButton {
        display: flex;
        align-items: center;
    }

    .uploadButtonIcon {
        align-self: center;
        margin: 0 0.75rem 0 -0.25rem;
    }

    @media all and (max-width: 960px) {
        .horizontalFields {
            flex-direction: column;
        }

        .title {
            &:first-of-type {
                margin: 0 0 0.5rem 0;
            }

            &:last-of-type {
                margin-left: 0;
            }
        }
    }
</style>